// # ui-no-screenshot-needed: auth provider with no markup of its own; sign-in moved from the hosted platform to Microsoft Entra
// Sign-in and session. Microsoft Entra signs the person in through MSAL (auth code + PKCE,
// redirect flow); the id token is posted to the API, which verifies it and sets an httpOnly
// session cookie. Roles per organization come from the API on every refresh.
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { PublicClientApplication, type AuthenticationResult } from "@azure/msal-browser";
import type { Claims, LiveRole } from "@/domain/types";
import { authApi, type SessionInfo } from "@/infra/cloud-adapter/client";
import { getAccessClaims } from "@/services/access";

const SCOPES = ["openid", "profile", "email"];
const RETRY_KEY = "oxley:redirect-retry";

interface AuthCtx {
  ready: boolean;
  claims: Claims | null;
  signInWithMicrosoft: (loginHint?: string) => Promise<{ error?: string }>;
  signInLocal: (email: string) => Promise<void>;
  signOut: () => Promise<void>;
  getSession: typeof authApi.getSession;
  getClaims: () => Promise<Claims | null>;
  refresh: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

let msalPromise: Promise<PublicClientApplication> | null = null;

/** One MSAL instance per page, configured from the API so the browser never hardcodes tenant ids. */
async function msal(): Promise<PublicClientApplication> {
  if (!msalPromise) {
    msalPromise = (async () => {
      const cfg = await authApi.config();
      if (!cfg.configured) throw new Error("Microsoft sign-in is not configured for this environment.");
      const app = new PublicClientApplication({
        auth: { clientId: cfg.client_id, authority: `https://login.microsoftonline.com/${cfg.tenant_id}`, redirectUri: cfg.redirect_uri, navigateToLoginRequestUrl: false },
        cache: { cacheLocation: "sessionStorage" },
      });
      await app.initialize();
      return app;
    })();
    msalPromise.catch(() => (msalPromise = null));
  }
  return msalPromise;
}

function toClaims(s: SessionInfo | null): Claims | null {
  if (!s) return null;
  return {
    sub: s.userId,
    email: s.email,
    tenant_ids: s.memberships.map((m) => m.tenant_id),
    roles_by_tenant: Object.fromEntries(s.memberships.map((m) => [m.tenant_id, m.role as LiveRole])),
    roles: [],
    duties: [],
    is_admin: s.memberships.some((m) => m.role === "tenant_admin"),
    actor_kind: "human",
  };
}

/** Session from the cookie, then the person's roles, duties and admin or support standing from the database. */
async function loadClaims(): Promise<Claims | null> {
  const s = await authApi.getSession();
  if (!s) return null;
  try {
    const access = await getAccessClaims();
    const role: LiveRole = access.duties.includes("packaging_edit") ? "pricing_strategist" : access.duties.includes("pricing_approve") ? "finance_approver" : access.is_admin ? "tenant_admin" : "viewer";
    return {
      sub: s.userId,
      email: s.email,
      tenant_ids: access.tenant_id ? [access.tenant_id] : [],
      roles_by_tenant: access.tenant_id ? { [access.tenant_id]: role } : {},
      roles: access.roles,
      duties: access.duties,
      is_admin: access.is_admin,
      actor_kind: access.actor_kind,
    };
  } catch {
    // Access claims unavailable (older API): fall back to the memberships the session carries.
    return toClaims(s);
  }
}

/** Finishes a Microsoft redirect if one is in flight. Returns true when the page is navigating away. */
async function completeRedirect(): Promise<boolean> {
  const returning = /(^|[#&?])(code|id_token|error)=/.test(window.location.hash + window.location.search);
  if (!returning) return false;
  try {
    const app = await msal();
    let result: AuthenticationResult | null = await app.handleRedirectPromise();
    if (!result && window.location.hash.includes("code=")) {
      // MSAL occasionally reports nothing on the very first pass while the response is still in the URL.
      await new Promise((r) => setTimeout(r, 300));
      result = await app.handleRedirectPromise();
    }
    if (result?.idToken) {
      await authApi.exchangeIdToken(result.idToken);
      sessionStorage.removeItem(RETRY_KEY);
      window.location.replace("/");
      return true;
    }
    if (window.location.hash.includes("code=") && !sessionStorage.getItem(RETRY_KEY)) {
      sessionStorage.setItem(RETRY_KEY, "1");
      window.location.reload();
      return true;
    }
    sessionStorage.removeItem(RETRY_KEY);
  } catch (e) {
    sessionStorage.removeItem(RETRY_KEY);
    console.error("Microsoft sign-in did not complete:", e);
  }
  return false;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [claims, setClaims] = useState<Claims | null>(null);
  const [ready, setReady] = useState(false);
  const refresh = useCallback(async () => {
    setClaims(await loadClaims());
    setReady(true);
  }, []);
  useEffect(() => {
    let live = true;
    void (async () => {
      if (await completeRedirect()) return;
      if (live) await refresh();
    })();
    const off = authApi.onChange(() => setClaims(null));
    return () => {
      live = false;
      off();
    };
  }, [refresh]);

  const value = useMemo<AuthCtx>(() => ({
    ready,
    claims,
    signInWithMicrosoft: async (loginHint?: string) => {
      try {
        const app = await msal();
        await app.loginRedirect({ scopes: SCOPES, prompt: "select_account", ...(loginHint ? { loginHint } : {}) });
        return {};
      } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) };
      }
    },
    signInLocal: async (email: string) => {
      await authApi.devLogin(email);
      await refresh();
    },
    signOut: async () => {
      await authApi.signOut();
      setClaims(null);
    },
    getSession: authApi.getSession,
    getClaims: loadClaims,
    refresh,
  }), [ready, claims, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth() {
  const c = useContext(Ctx);
  if (!c) throw new Error("useAuth outside AuthProvider");
  return c;
}

export const getSession = authApi.getSession;
